import { useState } from "react";
import { usePortalScope } from "@/analytics/PortalScope";
import { Button, Dialog, TextInput } from "@/components/ui";
import { useAuthedApi } from "@/api/useAuthedApi";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateOrgDialog({ open, onClose, onCreated }: Props) {
  const { companies } = useAuthedApi();
  // Opened only from the Members tab, so its portal names that path (D7).
  const scope = usePortalScope("members-create_org_dialog")["data-ph-scope"];
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleCreate() {
    if (!name.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await companies.createCompany(name.trim());
      setName("");
      onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message || "Could not create organization");
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    if (busy) return;
    setName("");
    setError(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} title="Create new organization" size="sm" scope={scope}>
      <div className="px-6 py-5 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="org-name" className="text-[13px] font-medium text-ink">
            Organization name
          </label>
          <TextInput
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Corp"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) handleCreate(); }}
          />
        </div>
        {error && (
          <p className="text-sm text-danger">{error}</p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button data-ph="cancel" variant="secondary" size="sm" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button data-ph="create" size="sm" onClick={handleCreate} disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
